import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ApnsConfiguration, GenerateCSRRequest } from "@/types/apns";
import { mockCustomers } from "@/utils/customerGroups";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HelpCircle, Download, Upload, ExternalLink, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ApnsConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialData?: ApnsConfiguration | null;
}

const COUNTRY_CODES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
  { code: "IN", name: "India" },
  // Add more as needed
];

export function ApnsConfigurationDialog({ 
  open, 
  onOpenChange, 
  onSuccess,
  initialData 
}: ApnsConfigurationDialogProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [configId, setConfigId] = useState<string | null>(initialData?.id || null);
  const [csrData, setCsrData] = useState<{ csr: string; filename: string } | null>(null);
  
  // Form fields
  const [name, setName] = useState(initialData?.name || "");
  const [customerId, setCustomerId] = useState(initialData?.customer_id || "global");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState(initialData?.email_address || "");
  const [certificateFile, setCertificateFile] = useState<File | null>(null);

  const handleGenerateCSR = async () => {
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Configuration name is required",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Auto-generate CSR fields based on configuration
      const selectedCustomer = mockCustomers.find(c => c.id === customerId);
      const organizationName = selectedCustomer?.name || "Global Organization";
      const commonName = `APNs Push - ${name}`;
      
      const isValidUuid = (value?: string) => {
        if (!value) return false;
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
      };
      
      const requestData: GenerateCSRRequest = {
        name,
        organizationName,
        commonName,
        countryCode: "US",
        emailAddress: email || undefined,
        customerId: (customerId !== "global" && isValidUuid(customerId)) ? customerId : undefined
      };

      // Step 1: Generate CSR
      const { data: csrResponse, error: csrError } = await supabase.functions.invoke('generate-apns-csr', {
        body: requestData
      });

      if (csrError) throw csrError;

      // Step 2: Sign CSR with MDM certificate
      const { data: signResponse, error: signError } = await supabase.functions.invoke('sign-apns-csr', {
        body: { configId: csrResponse.configId }
      });

      if (signError) throw signError;

      setConfigId(csrResponse.configId);
      setCsrData({ csr: signResponse.signedPlist, filename: signResponse.fileName });
      
      // Auto-download signed plist
      const blob = new Blob([signResponse.signedPlist], { type: 'application/x-plist' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = signResponse.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `Signed request (${signResponse.fileName}) downloaded! Upload to Apple's Push Certificates Portal.`,
      });
    } catch (error: any) {
      console.error('Error generating signed request:', error);
      
      // Extract detailed error message from edge function response
      let errorMessage = "Failed to generate signed request. Please check configuration and try again.";
      let errorTitle = "Error";
      
      // Parse error details from response
      const errorStr = error.message || String(error);
      
      // Check for specific error codes from backend
      if (errorStr.includes('crypto_engine_init_failed')) {
        errorTitle = "Cryptographic Engine Error";
        errorMessage = "Unable to initialize signing engine. This is a server configuration issue. Please contact support.";
      } else if (errorStr.includes('invalid_private_key_format')) {
        errorTitle = "Private Key Format Error";
        errorMessage = "The MDM private key must be in PKCS#8 format (BEGIN PRIVATE KEY). Please convert your key: openssl pkcs8 -topk8 -nocrypt -in oldkey.pem -out newkey.pem";
      } else if (errorStr.includes('invalid_private_key')) {
        errorTitle = "Private Key Import Error";
        errorMessage = "Cannot import MDM private key. Ensure it matches the certificate algorithm and is properly formatted.";
      } else if (errorStr.includes('invalid_certificate')) {
        errorTitle = "Certificate Parse Error";
        errorMessage = "Cannot parse MDM certificates. Verify all certificates (MDM, WWDR, Apple Root) are valid PEM files.";
      } else if (errorStr.includes('invalid_pem_format')) {
        errorTitle = "PEM Format Error";
        errorMessage = "One or more certificates/keys have invalid PEM formatting. Check headers and line breaks.";
      } else if (errorStr.includes('unsupported_algorithm')) {
        errorTitle = "Unsupported Algorithm";
        errorMessage = "The MDM certificate uses an unsupported algorithm. Only RSA and EC (P-256, P-384) are supported.";
      } else if (errorStr.includes('signing_failed')) {
        errorTitle = "Signing Failed";
        errorMessage = "Failed to sign the request. The private key may not match the certificate.";
      } else if (errorStr.includes('csr_generation_failed')) {
        errorTitle = "CSR Generation Failed";
        errorMessage = "Failed to generate Certificate Signing Request. Please check your input values.";
      } else if (errorStr.includes('missing_credentials')) {
        errorTitle = "Missing MDM Credentials";
        errorMessage = "MDM signing credentials not configured. Please add MDM_SIGNING_CERT, MDM_SIGNING_KEY, WWDR_CERT, and APPLE_ROOT_CERT secrets.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
        duration: 10000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadCSR = () => {
    if (!csrData) return;
    
    const blob = new Blob([csrData.csr], { type: 'application/x-plist' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csrData.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.pem')) {
        toast({
          title: "Invalid File",
          description: "Please upload a .pem certificate file",
          variant: "destructive"
        });
        return;
      }
      setCertificateFile(file);
    }
  };

  const handleUploadCertificate = async () => {
    if (!certificateFile || !configId) return;

    setIsUploading(true);
    try {
      const fileContent = await certificateFile.text();

      const { data, error } = await supabase.functions.invoke('validate-apns-certificate', {
        body: {
          configId,
          certificate: fileContent
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "APNs certificate uploaded and validated successfully!",
      });
      
      onOpenChange(false);
      onSuccess();
      resetForm();
    } catch (error: any) {
      console.error('Error uploading certificate:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload certificate",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCustomerId("global");
    setDescription("");
    setEmail("");
    setCertificateFile(null);
    setCsrData(null);
    setConfigId(null);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) resetForm();
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Edit" : "Add New"} APNs Configuration
          </DialogTitle>
        </DialogHeader>

        <TooltipProvider>
          <div className="space-y-4 pt-4">
            {/* Basic Configuration Fields */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Configuration Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production APNs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer">
                Assign to Customer <span className="text-destructive">*</span>
              </Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger id="customer">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (All Customers)</SelectItem>
                  {mockCustomers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this configuration..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="email">Apple ID Email (Optional)</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Email used when generating the certificate. Helpful for renewal reminders.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="apple.id@example.com"
              />
            </div>

            <Alert>
              <HelpCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium">Need help getting an APNs certificate?</p>
                  <a
                    href="https://identity.apple.com/pushcert/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    Open Apple Push Certificates Portal
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </AlertDescription>
            </Alert>

            {/* CSR Generation */}
            <div className="border-t pt-4">
              <Button 
                type="button"
                onClick={handleGenerateCSR} 
                disabled={isGenerating || !name.trim()}
                className="w-full gap-2"
              >
                <Download className="h-4 w-4" />
                {isGenerating ? "Generating & Signing..." : "Generate Signed Request"}
              </Button>
            </div>

            {/* Certificate Upload Section - Shows after CSR generation */}
            {csrData && (
              <>
                <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium text-green-900 dark:text-green-100">Signed Request Generated!</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadCSR}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download Signed Request Again
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>

                <Alert>
                  <HelpCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Upload APNs Certificate from Apple</p>
                      <ol className="list-decimal list-inside space-y-1 text-sm">
                        <li>Go to the <a href="https://identity.apple.com/pushcert/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                          Apple Push Certificates Portal
                          <ExternalLink className="h-3 w-3" />
                        </a></li>
                        <li>Upload the signed plist file ({csrData.filename})</li>
                        <li>Download the APNs certificate Apple provides (.pem)</li>
                        <li>Upload the APNs certificate below</li>
                      </ol>
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="cert">
                    Certificate File (.pem) <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="cert"
                      type="file"
                      accept=".pem"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                    {certificateFile && (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                  {certificateFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {certificateFile.name}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Footer Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {csrData && (
                <Button 
                  type="button"
                  onClick={handleUploadCertificate} 
                  disabled={!certificateFile || isUploading}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? "Uploading..." : "Upload Certificate"}
                </Button>
              )}
            </div>
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
